import { useState, useEffect } from "react";
import Layout from "@/components/layout/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Search, Loader2, Frown, X, Check, Star, ArrowLeft, ChevronDown, GitCompareArrows } from "lucide-react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { SEO } from "@/components/SEO";
import { useCompare } from "@/contexts/CompareContext";

const PAGE_SIZE = 10;

function escapeIlikePattern(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

const Systems = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { add: addToCompare, remove: removeFromCompare, has: hasInCompare, isFull: compareFull } = useCompare();
  
  const initialSearch = searchParams.get("search") || "";
  const initialCategory = searchParams.get("category") || "all";
  
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [submittedSearch, setSubmittedSearch] = useState(initialSearch);
  const [categoryId, setCategoryId] = useState<string>(initialCategory);
  const [sortBy, setSortBy] = useState<string>("newest");
  
  const [selectedSystem, setSelectedSystem] = useState<any>(null);

  // משיכת קטגוריות שיש להן לפחות מערכת אחת מאושרת
  const { data: categories } = useQuery({
    queryKey: ['categories', 'with-approved-systems'],
    queryFn: async (): Promise<any[]> => {
      const { data: cats } = await supabase
        .from('categories')
        .select('*')
        .eq('is_active', true);
        
      if (!cats || cats.length === 0) return [];

      const { data: activeSystems } = await supabase
        .from('systems')
        .select('category_id')
        .eq('status', 'approved');
        
      const activeCategoryIds = new Set((activeSystems || []).map((s: any) => s.category_id));

      return cats.filter((cat: any) => activeCategoryIds.has(cat.id));
    },
    staleTime: 1000 * 60 * 10,
  });

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['systems', submittedSearch, categoryId, sortBy],
    queryFn: async ({ pageParam = 0 }) => {
      let query = supabase
        .from('systems')
        .select(`*, category:categories(name_he), system_images(public_url, is_cover), ratings(stars)`)
        .eq('status', 'approved');

      if (submittedSearch) {
        const safePattern = escapeIlikePattern(submittedSearch);
        query = query.or(`title.ilike.%${safePattern}%,short_desc.ilike.%${safePattern}%`);
      }

      if (categoryId !== "all") query = query.eq('category_id', parseInt(categoryId));

      if (sortBy === "newest") query = query.order('created_at', { ascending: false });
      else if (sortBy === "oldest") query = query.order('created_at', { ascending: true });
      else if (sortBy === "name") query = query.order('title', { ascending: true });
      else if (sortBy === "rating") query = query.order('rating_avg', { ascending: false });

      query = query.range(pageParam, pageParam + PAGE_SIZE - 1);
      
      const { data, error } = await query;
      if (error) throw error;

      const systemsWithRatings = data.map((sys: any) => {
        const ratings = sys.ratings || [];
        const count = ratings.length;
        const avg = count > 0 ? ratings.reduce((acc: number, r: any) => acc + r.stars, 0) / count : 0;
        return {
          ...sys,
          rating_avg: sys.rating_avg || avg,
          rating_count: sys.rating_count || count,
          system_images: sys.system_images
        };
      });

      return {
        items: systemsWithRatings,
        nextCursor: data.length === PAGE_SIZE ? pageParam + PAGE_SIZE : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 0,
  });

  const allSystems = data?.pages.flatMap(p => p.items) || [];

  // Auto-select first system on desktop if nothing is selected
  useEffect(() => {
    if (allSystems.length > 0 && !selectedSystem && window.innerWidth >= 768) {
      setSelectedSystem(allSystems[0]);
    }
  }, [allSystems, selectedSystem]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedSearch(searchTerm);
    
    setSearchParams(prev => {
      if (searchTerm) {
        prev.set("search", searchTerm);
      } else {
        prev.delete("search");
      }
      return prev;
    });
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSubmittedSearch("");
    setCategoryId("all");
    setSearchParams({});
  };

  const handleCategoryChange = (newCategoryId: string) => {
    setCategoryId(newCategoryId);
    setSearchParams(prev => {
      if (newCategoryId !== "all") {
        prev.set("category", newCategoryId);
      } else {
        prev.delete("category");
      }
      return prev;
    });
  };

  const handleCardClick = (system: any) => {
    if (window.innerWidth < 768) {
      navigate(`/systems/${system.slug || system.id}`);
    } else {
      setSelectedSystem(system);
    }
  };

  const getCover = (sys: any) => {
    return sys?.system_images?.find((img: any) => img.is_cover)?.public_url 
        || sys?.system_images?.[0]?.public_url 
        || '/placeholder.svg';
  };

  const selectedSortLabel = {
    "newest": "חדש ביותר",
    "rating": "דירוג גולשים",
    "name": "שם (א-ת)",
    "oldest": "ישן ביותר"
  }[sortBy] || "מיון";

  const selectedCategoryLabel = categoryId === "all" 
    ? "כל הקטגוריות" 
    : categories?.find((c: any) => c.id.toString() === categoryId)?.name_he || "כל הקטגוריות";

  return (
    <Layout>
      <SEO 
        title="קטלוג מערכות" 
        description="גלו את המערכות החכמות והכלים המתקדמים ביותר בקטלוג המערכות של Feevho." 
      />
      
      <div className="bg-[#f8fafc] min-h-screen pb-12">
        
        {/* Header & Filters */}
        <div className="bg-white border-b border-slate-200/60 pb-4 pt-6 shadow-sm">
          <div className="container mx-auto px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-1">
              קטלוג מערכות
            </h1>
            <p className="text-sm md:text-base text-slate-500 font-normal mb-5">
              גלו את המערכות החכמות החדשות ביותר בשוק
            </p>
            
            {/* Filter Controls */}
            <div className="flex flex-col md:flex-row gap-3 w-full pb-2" dir="rtl">
              
              {/* Search Input */}
              <form onSubmit={handleSearchSubmit} className="relative w-full md:w-[340px] shrink-0">
                <Input
                  placeholder="חיפוש חופשי..."
                  className="pr-4 pl-14 h-12 rounded-[25px] border-slate-100 bg-white text-base shadow-[0_2px_16px_rgba(0,0,0,0.04)] w-full transition-all focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button 
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      setSubmittedSearch("");
                      setSearchParams(prev => {
                        prev.delete("search");
                        return prev;
                      });
                    }} 
                    className="absolute left-12 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                <button 
                  type="submit" 
                  className="absolute left-1.5 top-1/2 -translate-y-1/2 bg-slate-900 hover:bg-slate-800 text-white rounded-full h-9 w-9 flex items-center justify-center transition-colors shadow-sm"
                >
                  <Search className="h-4 w-4" />
                </button>
              </form>

              {/* Dropdowns container */}
              <div className="flex gap-3 w-full md:w-auto">
                
                {/* Category Dropdown (Visible only on Mobile) */}
                <div className="flex-1 md:hidden">
                  <DropdownMenu modal={false} dir="rtl">
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full h-12 rounded-[25px] border-slate-100 bg-white text-slate-700 shadow-[0_2px_16px_rgba(0,0,0,0.04)] font-medium hover:bg-slate-50 transition-colors px-4 flex justify-between items-center gap-2">
                        <span className="truncate">{selectedCategoryLabel}</span>
                        <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[calc(100vw-2rem)] rounded-2xl border-slate-100 shadow-lg p-1" align="center">
                      <DropdownMenuItem onClick={() => handleCategoryChange("all")} className="justify-between cursor-pointer py-2.5 px-3">
                        כל הקטגוריות
                        {categoryId === "all" && <Check className="w-4 h-4 text-blue-600" />}
                      </DropdownMenuItem>
                      {categories?.map((cat: any) => (
                        <DropdownMenuItem key={cat.id} onClick={() => handleCategoryChange(cat.id.toString())} className="justify-between cursor-pointer py-2.5 px-3">
                          {cat.name_he}
                          {categoryId === cat.id.toString() && <Check className="w-4 h-4 text-blue-600" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Sort Dropdown */}
                <div className="flex-1 md:flex-none">
                  <DropdownMenu modal={false} dir="rtl">
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full md:w-[160px] h-12 rounded-[25px] border-slate-100 bg-white text-slate-700 shadow-[0_2px_16px_rgba(0,0,0,0.04)] font-medium hover:bg-slate-50 transition-colors px-4 flex justify-between items-center gap-2">
                        <span className="truncate">{selectedSortLabel}</span>
                        <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[160px] rounded-2xl border-slate-100 shadow-lg p-1" align="end">
                      <DropdownMenuItem onClick={() => setSortBy("newest")} className="justify-between cursor-pointer py-2.5 px-3">
                        חדש ביותר
                        {sortBy === "newest" && <Check className="w-4 h-4 text-blue-600" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSortBy("rating")} className="justify-between cursor-pointer py-2.5 px-3">
                        דירוג גולשים
                        {sortBy === "rating" && <Check className="w-4 h-4 text-blue-600" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSortBy("name")} className="justify-between cursor-pointer py-2.5 px-3">
                        שם (א-ת)
                        {sortBy === "name" && <Check className="w-4 h-4 text-blue-600" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setSortBy("oldest")} className="justify-between cursor-pointer py-2.5 px-3">
                        ישן ביותר
                        {sortBy === "oldest" && <Check className="w-4 h-4 text-blue-600" />}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-6 py-6 md:py-8">
          
          <div className="mb-4 flex justify-end items-center text-sm text-slate-500 min-h-[24px]">
            <div className="font-medium"></div> 
            {(submittedSearch || categoryId !== "all") && (
              <button onClick={handleClearSearch} className="text-slate-900 font-bold hover:underline text-sm">נקה סינון</button>
            )}
          </div>

          <div className="flex flex-col md:flex-row gap-6 lg:gap-8 items-start">
            
            {/* Right Sidebar - Categories (Desktop) */}
            <div className="hidden md:flex w-[240px] lg:w-[260px] shrink-0 flex-col sticky top-[90px] h-fit max-h-[calc(100vh-110px)]">
              <div className="bg-white rounded-[20px] shadow-[0_4px_24px_rgba(0,0,0,0.04)] p-6 border border-slate-100 flex flex-col h-full overflow-hidden">
                <h3 className="font-bold text-xl mb-4 text-slate-900 border-b border-slate-100 pb-4 shrink-0">קטגוריות</h3>
                <div className="space-y-1.5 overflow-y-auto no-scrollbar -mx-2 px-2 flex-1">
                  <button
                    onClick={() => handleCategoryChange("all")}
                    className={`w-full text-right px-4 py-3 rounded-xl transition-all font-medium text-sm flex items-center justify-between ${
                      categoryId === "all"
                        ? "bg-slate-900 text-white"
                        : "bg-transparent text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    כל הקטגוריות
                    {categoryId === "all" && <Check className="w-4 h-4" />}
                  </button>
                  {categories?.map((cat: any) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.id.toString())}
                      className={`w-full text-right px-4 py-3 rounded-xl transition-all font-medium text-sm flex items-center justify-between ${
                        categoryId === cat.id.toString()
                          ? "bg-slate-900 text-white"
                          : "bg-transparent text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {cat.name_he}
                      {categoryId === cat.id.toString() && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Center Column - Systems List */}
            <div className="flex-1 flex flex-col gap-4 min-w-0 w-full">
              {isLoading ? (
                [...Array(6)].map((_, i) => (
                  <div key={i} className="flex gap-4 p-4 bg-white rounded-2xl border border-slate-100 shadow-[0_2px_16px_rgba(0,0,0,0.04)]">
                    <Skeleton className="w-16 h-16 md:w-20 md:h-20 rounded-2xl shrink-0" />
                    <div className="flex-1 space-y-2 py-1">
                      <Skeleton className="h-5 w-1/2" />
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-full mt-2" />
                    </div>
                  </div>
                ))
              ) : isError ? (
                <div className="text-center py-12 text-red-600 font-medium">אירעה שגיאה בטעינת המערכות.</div>
              ) : allSystems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-slate-200 rounded-3xl bg-white shadow-sm">
                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4"><Frown className="h-8 w-8 text-slate-400" /></div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">לא נמצאו תוצאות</h3>
                  <p className="text-slate-500 text-sm max-w-sm mx-auto mb-6 leading-relaxed">נסה לשנות את מילות החיפוש או לבחור קטגוריה אחרת.</p>
                  <Button variant="outline" onClick={handleClearSearch} className="rounded-xl px-8 border-slate-200 shadow-none font-bold">נקה סינון</Button>
                </div>
              ) : (
                <>
                  {allSystems.map((system) => {
                    const isSelected = selectedSystem?.id === system.id;
                    const isCompared = hasInCompare(system.id);
                    
                    return (
                      <div
                        key={system.id}
                        onClick={() => handleCardClick(system)}
                        className={`group flex items-start gap-4 md:gap-5 p-4 md:p-5 bg-white rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.04)] cursor-pointer transition-all duration-200 hover:shadow-lg border-2 ${
                          isSelected
                            ? 'md:border-slate-900'
                            : 'border-transparent hover:border-slate-100'
                        }`}
                      >
                        <img
                          src={getCover(system)}
                          alt={system.title}
                          className="w-16 h-16 md:w-[84px] md:h-[84px] rounded-2xl object-cover shrink-0 border border-slate-100 shadow-sm transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="flex-1 min-w-0 pt-0.5">
                          <div className="flex justify-between items-start gap-2 mb-1">
                            <h3 className="font-bold text-lg md:text-xl text-slate-900 truncate">{system.title}</h3>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isCompared) {
                                  removeFromCompare(system.id);
                                } else {
                                  addToCompare({
                                    id: system.id,
                                    title: system.title,
                                    slug: system.slug,
                                    short_desc: system.short_desc,
                                    cover_image: getCover(system),
                                    category_name: system.category?.name_he || null,
                                    rating_avg: system.rating_avg || 0,
                                    rating_count: system.rating_count || 0,
                                  });
                                }
                              }}
                              disabled={compareFull && !isCompared}
                              className={`shrink-0 w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center transition-colors ${
                                isCompared 
                                  ? "bg-slate-900 text-white shadow-sm" 
                                  : compareFull
                                    ? "bg-slate-50 text-slate-300 cursor-not-allowed"
                                    : "bg-slate-50 text-slate-400 hover:bg-slate-200 hover:text-slate-900 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100"
                              }`}
                              title={isCompared ? "הסר מהשוואה" : compareFull ? "ניתן להשוות עד 3" : "הוסף להשוואה"}
                            >
                              {isCompared ? <Check className="w-4 h-4 md:w-4 md:h-4" /> : <GitCompareArrows className="w-4 h-4 md:w-4 md:h-4" />}
                            </button>
                          </div>
                          <div className="flex items-center gap-1.5 mb-2">
                            <Star className="w-4 h-4 fill-[#f5a623] text-[#f5a623]" />
                            <span className="font-bold text-sm text-slate-700">
                              {(system.rating_avg || 0).toFixed(1)}
                            </span>
                            {system.rating_count > 0 && (
                              <span className="text-xs font-medium text-slate-400">({system.rating_count})</span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 line-clamp-2 leading-relaxed">
                            {system.short_desc}
                          </p>
                        </div>
                      </div>
                    );
                  })}

                  {hasNextPage && (
                    <div className="flex justify-center mt-8 pb-safe">
                      <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="rounded-[20px] px-8 h-12 border-slate-200 text-slate-700 hover:bg-slate-50 font-bold shadow-sm">
                        {isFetchingNextPage ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
                        {isFetchingNextPage ? "טוען..." : "הצג עוד מערכות"}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Left Panel - System Details (Desktop) */}
            {selectedSystem && (
              <div className="hidden lg:flex w-[340px] xl:w-[380px] shrink-0 flex-col sticky top-[90px] h-fit max-h-[calc(100vh-110px)]">
                <div className="bg-white rounded-[20px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] border border-slate-100 overflow-hidden flex flex-col h-full animate-in fade-in slide-in-from-left-4 duration-300">
                  
                  {/* Pastel Gradient Header */}
                  <div className="h-[120px] bg-gradient-to-br from-slate-100 to-slate-50/50 relative flex justify-center shrink-0 border-b border-slate-50">
                    <div className="absolute -bottom-10 p-1 bg-white rounded-[1.25rem] shadow-md">
                      <img
                        src={getCover(selectedSystem)}
                        className="w-[84px] h-[84px] rounded-xl object-cover"
                        alt={selectedSystem.title}
                      />
                    </div>
                  </div>
                  
                  <div className="pt-14 px-6 pb-6 flex flex-col flex-1 overflow-y-auto no-scrollbar">
                    <div className="text-center mb-6 shrink-0">
                      <h2 className="font-black text-2xl text-slate-900 mb-1 leading-tight">{selectedSystem.title}</h2>
                      <p className="text-sm font-medium text-slate-500 mb-3">{selectedSystem.category?.name_he}</p>
                      
                      <div className="flex justify-center items-center gap-1.5 inline-flex bg-slate-50 px-3 py-1.5 rounded-full border border-slate-100">
                        <Star className="w-4 h-4 fill-[#f5a623] text-[#f5a623]" />
                        <span className="font-bold text-base text-slate-900">
                          {(selectedSystem.rating_avg || 0).toFixed(1)}
                        </span>
                        {selectedSystem.rating_count > 0 && (
                          <span className="text-xs font-medium text-slate-400 ms-1">({selectedSystem.rating_count} דירוגים)</span>
                        )}
                      </div>
                    </div>

                    <p className="text-sm text-slate-600 leading-relaxed mb-8 text-center px-1 shrink-0">
                      {selectedSystem.short_desc}
                    </p>

                    <div className="mt-auto pt-2 shrink-0">
                      <Button asChild className="w-full h-14 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-base shadow-lg shadow-slate-200 transition-transform active:scale-[0.98]">
                        <Link to={`/systems/${selectedSystem.slug || selectedSystem.id}`}>
                          כניסה למערכת
                          <ArrowLeft className="w-5 h-5 me-2" />
                        </Link>
                      </Button>
                    </div>
                  </div>

                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Systems;